import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    HizliIslemButonlari,
    GunlukIhtiyaclarListesi,
    PlanliGiderlerListesi,
    HizliTahsilatModal,
    HizliOdemeModal,
    KarsilamaBasligi
} from './components';

// Mock Data
import { mockPayments } from '@mocks/odemeler';
import { mockExpenses } from '@mocks/harcamalar';
import { mockInstallments } from '@mocks/taksitler';

// Not: mockApartments modal içinde import ediliyor, burada gerekirse kullanılır.

export default function Dashboard() {
    // -------------------------------------------------------------------------
    // STATE YÖNETİMİ
    // -------------------------------------------------------------------------
    const [payments, setPayments] = useState([...mockPayments]);
    const [expenses, setExpenses] = useState([...mockExpenses]);
    const [installments, setInstallments] = useState([...mockInstallments]);

    // Modallar
    const [incomeModalOpen, setIncomeModalOpen] = useState(false);
    const [expenseModalOpen, setExpenseModalOpen] = useState(false);

    // -------------------------------------------------------------------------
    // HESAPLAMALAR (Derived State)
    // -------------------------------------------------------------------------

    // 1. Operasyonel Liste: Yaklaşan ve Geciken Taksitler
    const dailyNeeds = useMemo(() => {
        const overdue = installments.flatMap(inst =>
            inst.payments.filter(p => p.status === 'overdue').map(p => ({
                id: `${inst.id}-${p.no}`,
                title: `${inst.personName} - ${inst.apartmentNo}`,
                subtitle: `Geciken Taksit (${p.no}. Taksit)`,
                amount: p.amount,
                isOverdue: true,
                dueDate: p.dueDate
            }))
        );

        const upcoming = installments.flatMap(inst =>
            inst.payments.filter(p => p.status === 'pending').slice(0, 1).map(p => ({
                id: `${inst.id}-${p.no}`,
                title: `${inst.personName} - ${inst.apartmentNo}`,
                subtitle: `Vade: ${new Date(p.dueDate).toLocaleDateString('tr-TR')}`,
                amount: p.amount,
                isOverdue: false,
                dueDate: p.dueDate
            }))
        ).slice(0, 5); // Sadece 5 tane göster

        return [...overdue, ...upcoming];
    }, [installments]);

    // 2. Planlı Giderler (Simülasyon)
    const plannedExpenses = useMemo(() => [
        { id: 'pe-1', category: 'İşçilik', description: 'Haftalık Usta Ödemesi', amount: 12000, date: '2026-02-16' },
        { id: 'pe-2', category: 'Kira', description: 'Ofis Kirası', amount: 4500, date: '2026-02-20' },
        { id: 'pe-3', category: 'Fatura', description: 'Elektrik Faturası', amount: 2300, date: '2026-02-18' },
    ], []);

    // 3. Akıllı Uyarılar için Metrikler -> WelcomeHeader'a gönderilecek
    const overdueCount = dailyNeeds.filter(i => i.isOverdue).length;
    const todayCollectionAmount = dailyNeeds.reduce((sum, item) => sum + item.amount, 0);

    // Formatlayıcı
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // -------------------------------------------------------------------------
    // EVENT HANDLERS
    // -------------------------------------------------------------------------
    const handleIncomeSubmit = (data) => {
        // 1. Ödeme Kaydı
        const newPayment = {
            id: `pay-new-${Date.now()}`,
            constructionId: data.construction.id,
            apartmentId: data.apartment.id,
            type: 'income',
            category: 'Daire Satışı',
            amount: data.amount,
            date: data.date,
            description: data.description,
        };
        setPayments(prev => [newPayment, ...prev]);

        // 2. Toast Bildirimi
        toast.success(
            <div>
                <div className="font-bold">Gelir Eklendi</div>
                <div className="text-sm">{data.description} - {formatCurrency(data.amount)}</div>
            </div>
        );

        if (data.newCustomer) {
            toast.info(`Yeni Müşteri Oluşturuldu: ${data.newCustomer.name}`);
        }
    };

    const handleExpenseSubmit = (data) => {
        const newExpense = {
            id: `exp-new-${Date.now()}`,
            constructionId: null,
            type: 'expense',
            category: data.category,
            amount: data.amount,
            date: data.date,
            description: data.description,
            paymentMethod: data.paymentMethod
        };
        setExpenses(prev => [newExpense, ...prev]);

        // 2. Toast Bildirimi (Gider - Belki turuncu/kırmızı tonlu?)
        // Standart success kullanalım, kafa karıştırmasın. İşlem başarılı.
        toast.success(
            <div>
                <div className="font-bold">Gider Kaydedildi</div>
                <div className="text-sm">{data.category} - {formatCurrency(data.amount)}</div>
            </div>
        );
    };

    // "Tahsil Et" butonu için handler (Şimdilik IncomeModal'ı açar)
    const handleQuickCollect = (item) => {
        setIncomeModalOpen(true);
    };

    // "Ödendi" butonu için handler
    const handleQuickPay = (item) => {
        toast.success(`${item.description} ödendi olarak işaretlendi.`);
        // Listeden çıkarma mantığı eklenebilir.
    };

    // -------------------------------------------------------------------------
    // RENDER
    // -------------------------------------------------------------------------
    return (
        <div className="w-full space-y-6 pb-10">
            {/* 1. WELCOME HEADER (Geliştirilmiş - Sağ Taraf Dolu) */}
            <KarsilamaBasligi
                overdueCount={overdueCount}
                todayCollectionAmount={todayCollectionAmount}
                formatCurrency={formatCurrency}
            />

            {/* 2. HIZLI AKSİYON BUTONLARI (Vibrant Design) */}
            <section>
                <HizliIslemButonlari
                    onIncomeClick={() => setIncomeModalOpen(true)}
                    onExpenseClick={() => setExpenseModalOpen(true)}
                />
            </section>

            {/* 3. OPERASYONEL LİSTE (Grid) */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[400px]">
                {/* Sol: Alacaklar / Taksitler */}
                <GunlukIhtiyaclarListesi
                    title="Yaklaşan Taksitler & Ödemeler"
                    items={dailyNeeds}
                    onItemAction={handleQuickCollect}
                    actionLabel="Tahsil Et"
                    emptyMessage="Harika! Bekleyen tahsilat yok."
                    formatCurrency={formatCurrency}
                />

                {/* Sağ: Planlı Giderler */}
                <PlanliGiderlerListesi
                    items={plannedExpenses}
                    onPayAction={handleQuickPay}
                    formatCurrency={formatCurrency}
                />
            </section>

            {/* Modallar */}
            <HizliTahsilatModal
                isOpen={incomeModalOpen}
                onClose={() => setIncomeModalOpen(false)}
                onSubmit={handleIncomeSubmit}
            />

            <HizliOdemeModal
                isOpen={expenseModalOpen}
                onClose={() => setExpenseModalOpen(false)}
                onSubmit={handleExpenseSubmit}
            />
        </div>
    );
}
